import { useState } from "react";
import { useParams } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { pink } from "@mui/material/colors";

import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { v4 as uuidv4 } from 'uuid';

import { useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    EditableDataGrid,
    FilledButton, GeneralChip,
    RowWithId, Typography, useSnackbar
} from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, } from "@initia-digital/ts-utils";
import { IBooking, IContact, } from "@initia-digital/types/work-and-safety";
import {
    BOOKINGS_COL_DEFS, NEW_BOOKING_ROW_TEMPLATE,
    CONTACT_COL_DEFS, NEW_CONTACT_ROW_TEMPLATE,
} from "./DataGridConfiguration";


import { useWorkAndSafetyStore } from "../store";
import { fetchContractorList, fetchContractorTypeList } from "../api/query";
import { PlanActions } from "./PlanActions";
import { IPlanStageProps } from "../interfaces/componentInterfaces";
import { ContractorsAutoComplete, } from "./Autosuggest";
import { SectionHeader } from "./SectionHeader";
import { PlanHeader } from "./PlanHeader";
import { ContactPersonAlert } from "./ContactPersonAlert";


export const PlanContactsAndBookings = (props: IPlanStageProps) => {
    // -------------------------------
    const {
        handleSave, handleSaveAndNext,
        handleStatusChange
    } = props;
    const { editPlanState, updateEditPlanState } = useWorkAndSafetyStore();
    const {
        title, status,
        contractorsAndSubcontractors, bookings,
        contacts
    } = editPlanState;

    // STATE
    const [selectedRows, setSelectedRows] = useState<{
        tests: RowWithId[]
        installations: RowWithId[]
        bookings: RowWithId[]
        contacts: RowWithId[]
    }>({
        tests: [],
        installations: [],
        bookings: [],
        contacts: []
    });

    const params = useParams();
    const { planId, jobId } = params;

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const { queueSnackbar } = useSnackbar();

    const authDetail = useMSALPayload();

    const { isLoading: isContractorListLoading, data: contractorList } = useQuery({
        queryKey: ['contractorListMin'],
        queryFn: () => fetchContractorList(authDetail),
    });
    const { isLoading: isContractorTypeListLoading, data: contractorTypeList } = useQuery({
        queryKey: ['contractorTypeList'],
        queryFn: () => fetchContractorTypeList(authDetail),
    });


    const dynamicBookingColumnDefs = !isContractorTypeListLoading && baseDefinedAndNotNullNarrowing(contractorTypeList) ? BOOKINGS_COL_DEFS.map(colDef => {
        if (colDef.field === 'bookingType') {
            return {
                ...colDef,
                type: 'singleSelect',
                valueOptions: contractorTypeList?.map(type => type.label) ?? []
            } as GridColDef<IBooking>;
        }
        return colDef;
    }) : BOOKINGS_COL_DEFS;


    return (
        <Box sx={{ width: '100%' }}>
            <PlanHeader
                id={planId}
                title={title}
                status={status}
            />
            <Box
                display="flex" flexDirection={isSmallerThanSm ? "column" : "row"} flexWrap="wrap"
                alignItems="center" justifyContent="space-between"
                maxWidth="100%"
            >
                <SectionHeader label="Contacts and Bookings" />
                <PlanActions
                    handleSave={handleSave}
                    handleStatusChange={handleStatusChange}
                />
            </Box>

            <Box display="flex" flexDirection="column" my="10px">
                <Box>
                    <Typography
                        fontSize={"0.9rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        {`Contractors and Subcontractors (${contractorsAndSubcontractors.length})`}
                    </Typography>
                </Box>
                <Box>
                    <Typography
                        fontSize={"0.7rem"}
                        fontWeight={300}
                        color={theme.palette.primary.contrastText}
                    >
                        A contractors main contact will be added to the contacts list if available
                    </Typography>
                </Box>
                <ContractorsAutoComplete
                    name="contractorsAndSubcontractors"
                    label=""
                    handleAutoSuggestSelectStateChange={(event) => {
                        // ---------------------
                        updateEditPlanState({
                            ...editPlanState,
                            [event.target.name]: event.target.value
                        });
                    }}
                    selectedValues={contractorsAndSubcontractors}
                />
                {
                    contractorsAndSubcontractors.length === 0 &&
                    <Box my="10px" mx="20px">
                        <Typography
                            fontSize={"0.8rem"}
                            fontWeight={300}
                            color={theme.palette.primary.contrastText}
                        >
                            No contractors or subcontractors
                        </Typography>
                    </Box>
                }
                {
                    contractorsAndSubcontractors.length > 0 &&
                    <Box
                        display="flex" flexDirection="row" flexWrap="wrap" rowGap="5px" columnGap="5px"
                        alignItems="center" justifyContent="flex-start"
                        m="5px"
                    >
                        {
                            contractorsAndSubcontractors.map(contractor => {
                                return (
                                    <GeneralChip
                                        key={`contractorchip-${contractor}`}
                                        label={contractor}
                                        backgroundColor={theme.palette.secondary.main}
                                        size="small"
                                        clickable
                                        handleClick={() => {
                                            // ---------------------
                                            if (!isContractorListLoading && baseDefinedAndNotNullNarrowing(contractorList)) {
                                                // --------------------------------------------
                                                const contractorSearch = contractorList.find(c => c.label === contractor);

                                                if (baseDefinedAndNotNullNarrowing(contractorSearch)) {
                                                    // --------------------------------------------
                                                    updateEditPlanState({
                                                        ...editPlanState,
                                                        bookings: {
                                                            rows: [
                                                                ...editPlanState.bookings.rows,
                                                                {
                                                                    ...NEW_BOOKING_ROW_TEMPLATE,
                                                                    id: uuidv4(),
                                                                    jobId: jobId ?? '',
                                                                    contractorOrSubcontractor: contractorSearch.label,
                                                                    bookingType: contractorSearch.type
                                                                }
                                                            ]
                                                        }
                                                    });
                                                }
                                                else {
                                                    // --------------------------------------------
                                                    updateEditPlanState({
                                                        ...editPlanState,
                                                        bookings: {
                                                            rows: [
                                                                ...editPlanState.bookings.rows,
                                                                {
                                                                    ...NEW_BOOKING_ROW_TEMPLATE,
                                                                    id: uuidv4(),
                                                                    jobId: jobId ?? '',
                                                                    contractorOrSubcontractor: contractor
                                                                }
                                                            ]
                                                        }
                                                    });
                                                }
                                            }
                                            else {
                                                // --------------------------------------------
                                                queueSnackbar(
                                                    `The contractor list was still loading, please try to add this booking again`,
                                                    'info',
                                                    'Please try to add booking again',
                                                    3000
                                                );
                                                return;
                                            }
                                        }}
                                        handleDelete={() => {
                                            // ---------------------
                                            updateEditPlanState({
                                                ...editPlanState,
                                                contractorsAndSubcontractors: contractorsAndSubcontractors.filter(c => c !== contractor)
                                            });
                                        }}
                                    />
                                )
                            })
                        }
                    </Box>
                }
            </Box>



            <Box display="flex" flexDirection="column" my="10px">
                <Box>
                    <Typography
                        fontSize={"0.9rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        {`Contacts (${contacts.rows.length})`}
                    </Typography>
                </Box>

                {
                    contractorsAndSubcontractors.length > 0 &&
                    <>
                        <Box>
                            <Typography
                                fontSize={"0.7rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                You can automatically add known key contractor contacts using the button below.
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            mb="5px"
                        >
                            <FilledButton
                                size="vsmall"
                                onClick={() => {
                                    // ---------------------
                                    if (isContractorListLoading || !baseDefinedAndNotNullNarrowing(contractorList)) {
                                        // --------------------------------------------
                                        queueSnackbar(
                                            `The contractor list was still loading and the key contact could not be automatically added`,
                                            'info',
                                            'Please try to add contacts again',
                                            3000
                                        );
                                        return;
                                    }
                                    else if (contractorsAndSubcontractors.length > 0) {
                                        // --------------------------------------------
                                        let editPlanStateUpdate = {
                                            ...editPlanState
                                        };
                                        for (const contractor of contractorsAndSubcontractors) {
                                            // --------------------------------------------
                                            const contractorSearch = contractorList.find(c => c.label === contractor);
                                            if (baseDefinedAndNotNullNarrowing(contractorSearch)) {
                                                // --------------------------------------------
                                                const { contactName, contactEmail, contactPhone, label } = contractorSearch;
                                                const hasContactDetail = baseDefinedAndNotNullNarrowing(contactName) || baseDefinedAndNotNullNarrowing(contactEmail) || baseDefinedAndNotNullNarrowing(contactPhone);
                                                if (hasContactDetail) {
                                                    // --------------------------------------------
                                                    editPlanStateUpdate = {
                                                        ...editPlanStateUpdate,
                                                        contacts: {
                                                            rows: [
                                                                ...editPlanStateUpdate.contacts.rows,
                                                                {
                                                                    ...NEW_CONTACT_ROW_TEMPLATE,
                                                                    id: uuidv4(),
                                                                    jobId: jobId ?? '',
                                                                    planId: planId ?? '',
                                                                    contactName: contactName ?? '',
                                                                    contactOrg: label ?? '',
                                                                    contactEmail: contactEmail ?? '',
                                                                    contactNumber: contactPhone ?? '',
                                                                    isOrgMain: true
                                                                }
                                                            ]
                                                        }
                                                    };
                                                }
                                            }
                                        }
                                        updateEditPlanState(editPlanStateUpdate);
                                    }
                                }}
                            >
                                Include contractors
                            </FilledButton>
                        </Box>
                    </>
                }

                

                <EditableDataGrid<IContact>
                    density="compact"
                    singleClickEdit
                    disableColumnMenu
                    disableColumnSorting
                    disableColumnResize
                    hideFooter
                    rows={contacts.rows}
                    columns={CONTACT_COL_DEFS}
                    onRowsChange={(newRows) => {
                        // ---------------------
                        updateEditPlanState({
                            ...editPlanState,
                            contacts: {
                                rows: newRows
                            }
                        });
                    }}
                    onRowSelection={(updateSelectedRows) => {
                        // ---------------------
                        setSelectedRows({
                            ...selectedRows,
                            contacts: updateSelectedRows
                        });
                    }}
                    generateNewId={() => uuidv4()}
                    addRowTemplate={{
                        ...NEW_CONTACT_ROW_TEMPLATE,
                        jobId: jobId ?? ''
                    }}
                />

                <Box
                    display="flex"
                    flexDirection="column"
                    gap="4px"
                    mt="16px"
                >
                    <Typography
                        color={theme.palette.primary.contrastText}
                        fontSize={"0.9rem"}
                        fontWeight={700}
                    >
                        Site Contact
                    </Typography>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap="0px"
                    >
                    <Typography
                        color={pink[500]}
                        fontWeight={300}
                        fontSize="0.6rem"
                    >
                        Who is responsible for the site? The client, land owner, or a facilities manager are common examples
                    </Typography>
                    <Typography
                        color={theme.palette.primary.contrastText}
                        fontWeight={300}
                        fontSize="0.65rem"
                    >
                        {contacts.rows.filter(contact => contact.contactName).length === 0
                            ? "Add contacts above to enable site contact selection"
                            : "Select a site contact from the contacts list"
                        }
                    </Typography>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap="8px"
                    >
                        {/* Contact options */}
                        {contacts.rows
                            .filter(contact => contact.contactName)
                            .map((contact) => (
                                <GeneralChip
                                    key={`main-contact-${contact.id}`}
                                    label={contact.contactName}
                                    backgroundColor={editPlanState.siteContactId === contact.id ? pink[500] : theme.palette.primary.main}
                                    color={editPlanState.siteContactId === contact.id ? theme.palette.common.white : theme.palette.primary.contrastText}
                                    size="small"
                                    clickable
                                    handleClick={() => {
                                        updateEditPlanState({
                                            ...editPlanState,
                                            siteContactId: editPlanState.siteContactId === contact.id ? null : contact.id
                                        });
                                    }}
                                />
                            ))}
                    </Box>

                    <ContactPersonAlert 
                        contactPersonName={
                            editPlanState.siteContactId 
                                ? contacts.rows.find(contact => contact.id === editPlanState.siteContactId)?.contactName
                                : null
                        }
                        successMessage="Site contact selected"
                        warningMessage={
                            contacts.rows.filter(contact => contact.contactName).length === 0
                                ? "No contacts have been added - add contacts above to select a site contact"
                                : "Site contact has not been selected"
                        }
                        showPersonName={true}
                    />
                </Box>
            </Box>

            <Box display="flex" flexDirection="column" my="10px" id="bookings-section">
                <Box>
                    <Typography
                        fontSize={"0.9rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        {`Bookings (${bookings.rows.length})`}
                    </Typography>
                </Box>
                {
                    contractorsAndSubcontractors.length > 0 &&
                    <>
                        <Box>
                            <Typography
                                fontSize={"0.7rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                You can automatically add all contractors as bookings using the button below.
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            mb="5px"
                        >
                            <FilledButton
                                size="vsmall"
                                onClick={() => {
                                    // ---------------------
                                    if (isContractorListLoading || !baseDefinedAndNotNullNarrowing(contractorList)) {
                                        // --------------------------------------------
                                        queueSnackbar(
                                            `The contractor list was still loading and the bookings could not be automatically added`,
                                            'info',
                                            'Please try to add bookings again',
                                            3000
                                        );
                                        return;
                                    }
                                    else if (contractorsAndSubcontractors.length > 0) {
                                        // --------------------------------------------
                                        let editPlanStateUpdate = {
                                            ...editPlanState
                                        };
                                        for (const contractor of contractorsAndSubcontractors) {
                                            // --------------------------------------------
                                            const contractorSearch = contractorList.find(c => c.label === contractor);

                                            if (baseDefinedAndNotNullNarrowing(contractorSearch)) {
                                                // --------------------------------------------
                                                editPlanStateUpdate = {
                                                    ...editPlanStateUpdate,
                                                    bookings: {
                                                        rows: [
                                                            ...editPlanStateUpdate.bookings.rows,
                                                            {
                                                                ...NEW_BOOKING_ROW_TEMPLATE,
                                                                id: uuidv4(),
                                                                jobId: jobId ?? '',
                                                                contractorOrSubcontractor: contractorSearch.label,
                                                                bookingType: contractorSearch.type
                                                            }
                                                        ]
                                                    }
                                                };
                                            }
                                            else {
                                                // --------------------------------------------
                                                editPlanStateUpdate = {
                                                    ...editPlanStateUpdate,
                                                    bookings: {
                                                        rows: [
                                                            ...editPlanStateUpdate.bookings.rows,
                                                            {
                                                                ...NEW_BOOKING_ROW_TEMPLATE,
                                                                id: uuidv4(),
                                                                jobId: jobId ?? '',
                                                                contractorOrSubcontractor: contractor
                                                            }
                                                        ]
                                                    }
                                                };
                                            }
                                        }
                                        updateEditPlanState(editPlanStateUpdate);
                                    }
                                }}
                            >
                                Include contractors
                            </FilledButton>
                        </Box>
                    </>
                }
                <Box>
                    <EditableDataGrid<IBooking>
                        density="compact"
                        singleClickEdit
                        disableColumnMenu
                        disableColumnSorting
                        disableColumnResize
                        hideFooter
                        rows={bookings.rows}
                        columns={dynamicBookingColumnDefs}
                        multilineFields={['bookingNotes']}
                        onRowsChange={(newRows) => {
                            // ---------------------
                            updateEditPlanState({
                                ...editPlanState,
                                bookings: {
                                    rows: newRows
                                }
                            });
                        }}
                        onRowSelection={(updateSelectedRows) => {
                            // ---------------------
                            setSelectedRows({
                                ...selectedRows,
                                bookings: updateSelectedRows
                            });
                        }}
                        generateNewId={() => uuidv4()}
                        addRowTemplate={{
                            ...NEW_BOOKING_ROW_TEMPLATE,
                            jobId: jobId ?? ''
                        }}
                    />
                    {
                        !isSmallerThanSm &&
                        <Box>
                            <Typography
                                fontSize={"0.7rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                When you finished editing Notes, <strong>click away</strong> OR press <strong>CTRL+ENTER</strong> to close
                            </Typography>
                        </Box>
                    }
                </Box>
            </Box>

            <Box
                width="100%"
                display="flex" flexDirection="row" justifyContent="flex-end"
                pt="12px"
            >
                <FilledButton
                    onClick={handleSaveAndNext}
                    endIcon={<NavigateNextIcon />}
                >
                    Save and Next
                </FilledButton>
            </Box>
        </Box>
    )
}